import React from 'react'

import VerificationModal from '../../components/molecules/ChatModals/VerificationModal'
import ErrorToast from '../../components/molecules/ErrorToast'

import { verifySession } from '../../lib/api'
import useAccess, { PERMS } from '../../hooks/useAccess'
import { useQueueState } from '../QueueStateProvider'
import { useEnvironment } from '../EnvironmentProvider'
import { useUserAPI } from '../UserAPIProvider'
import useStyles from './styles'

const VerificationContext = React.createContext(null)

const VerificationProvider = ({ children }) => {
  const { conversation } = useQueueState()
  const hasPermission = useAccess()
  const { user } = useEnvironment()
  const { allUsers } = useUserAPI()

  const [showModal, setShowModal] = React.useState(false)
  const [owner, setOwner] = React.useState(null)
  const [error, setError] = React.useState(null)
  const lastOwnerSet = React.useRef(null)

  const verified = React.useMemo(() => {
    // Conversations count as verified if they have a verification timestamp
    return Boolean(conversation?.verified?.stamp)
  }, [conversation])

  const canVerify = React.useMemo(() => {
    if (hasPermission(PERMS.ConversationsFull)) {
      return true
    }

    if (hasPermission(PERMS.ConversationsBasic)) {
      if (!conversation || !user) {
        return false
      }

      const { owner_id, assigned_to } = conversation
      return [owner_id, assigned_to].includes(user.id)
    }

    return false
  }, [hasPermission, conversation, user])

  const classes = useStyles()

  // Update owner from cache
  React.useEffect(() => {
    const ownerID = conversation?.owner_id
    if (ownerID && lastOwnerSet.current !== ownerID) {
      lastOwnerSet.current = ownerID
      setOwner(allUsers[ownerID])
    }
  }, [conversation, allUsers])

  // Link that shows in the ui to start verification
  const link = React.useMemo(() => {
    if (verified || !canVerify) {
      return null
    }

    return (
      <div onClick={() => setShowModal(true)} className={classes.link}>
        Click here to verify this user
      </div>
    )
  }, [verified, canVerify, classes.link])

  const closeModal = React.useCallback(() => {
    setShowModal(false)
    setError(null)
  }, [])

  const performVerification = React.useCallback(async () => {
    try {
      await verifySession(conversation.session)
      closeModal()
    } catch (err) {
      setError({ err })
    }
  }, [conversation, closeModal])

  return (
    <VerificationContext.Provider value={{ verified, link, owner }}>
      {children}

      {showModal && (
        <VerificationModal
          name={owner?.name}
          onClose={closeModal}
          onAccept={performVerification}
        />
      )}

      <ErrorToast error={error} />
    </VerificationContext.Provider>
  )
}

export default VerificationProvider

export const useVerification = () => React.useContext(VerificationContext)
